import React, { useState, useEffect } from 'react';
import api from '../../api';

function FilterPanel({ filters, companies, onFilterChange }) {
  const [branches, setBranches] = useState([]);
  const [localFilters, setLocalFilters] = useState(filters);

  // Fetch branches when company changes
  useEffect(() => {
    async function fetchBranches() {
      if (localFilters.companyId) {
        try {
          const response = await api.get(`/companies/${localFilters.companyId}`);
          setBranches(response.data.Branches || []);
        } catch (err) {
          console.error('Error fetching branches:', err);
          setBranches([]);
        }
      } else {
        setBranches([]);
      }
    }
    fetchBranches();
  }, [localFilters.companyId]);

  // Handle filter changes
  const handleChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    
    // Reset branch filter when company changes
    if (key === 'companyId') {
      newFilters.branchId = null;
    }

    // Clear company filter when switching to all view
    if (key === 'view' && value === 'all') {
      newFilters.companyId = null;
      newFilters.branchId = null;
    }

    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Reset all filters
  const handleReset = () => {
    const resetFilters = {
      view: 'all',
      companyId: null,
      branchId: null,
      startDate: null,
      endDate: null,
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  // Set date range shortcuts
  const setDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;

    switch (range) {
      case 'today':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 7);
        break;
      case 'month':
        startDate = new Date(today);
        endDate = new Date(today);
        endDate.setMonth(endDate.getMonth() + 1);
        break;
      case 'quarter':
        startDate = new Date(today);
        endDate = new Date(today);
        endDate.setMonth(endDate.getMonth() + 3);
        break;
      default:
        return;
    }

    const newFilters = {
      ...localFilters,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <div className="space-y-4">
        {/* View Toggle */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            View Mode
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleChange('view', 'all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                localFilters.view === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Companies
            </button>
            <button
              onClick={() => handleChange('view', 'single')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                localFilters.view === 'single'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Single Company
            </button>
          </div>
        </div>

        {/* Company Selector (only for single view) */}
        {localFilters.view === 'single' && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Company
            </label>
            <select
              value={localFilters.companyId || ''}
              onChange={(e) => handleChange('companyId', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a company...</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Branch Selector (only when company is selected) */}
        {localFilters.companyId && branches.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Branch (Optional)
            </label>
            <select
              value={localFilters.branchId || ''}
              onChange={(e) => handleChange('branchId', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date Range */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Date Range
          </label>
          
          {/* Quick Date Shortcuts */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setDateRange('today')}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => setDateRange('week')}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Next Week
            </button>
            <button
              onClick={() => setDateRange('month')}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Next Month
            </button>
            <button
              onClick={() => setDateRange('quarter')}
              className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Next Quarter
            </button>
          </div>

          {/* Custom Date Range */}
          <div className="space-y-2">
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Start Date</label>
              <input
                type="date"
                value={localFilters.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">End Date</label>
              <input
                type="date"
                value={localFilters.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(localFilters.companyId || localFilters.branchId || localFilters.startDate || localFilters.endDate) && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-2">Active Filters:</h4>
            <div className="space-y-1 text-xs text-blue-700 dark:text-blue-400">
              {localFilters.companyId && (
                <div>• Company: {companies.find(c => c.id === localFilters.companyId)?.name}</div>
              )}
              {localFilters.branchId && (
                <div>• Branch: {branches.find(b => b.id === localFilters.branchId)?.name}</div>
              )}
              {localFilters.startDate && (
                <div>• From: {new Date(localFilters.startDate).toLocaleDateString()}</div>
              )}
              {localFilters.endDate && (
                <div>• To: {new Date(localFilters.endDate).toLocaleDateString()}</div>
              )}
            </div>
          </div>
        )}
      
      {/* Reset Button */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleReset}
          className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Reset Filters</span>
        </button>
      </div>
    </div>
  );
}

export default FilterPanel;

